import { useState, useEffect, useMemo } from "react";
import { Sv, ic } from "../../components/common/Icons";
import api from "../../services/api";import "../../assets/styles/ITAdmin/ITAExitPg.css";


// ── helpers ──
const monthKey  = (d) => { try { const dt=new Date(d); return `${dt.getFullYear()}-${String(dt.getMonth()+1).padStart(2,"0")}`; } catch{return null;} };
const monthLabel= (k) => { if(!k)return""; const [y,m]=k.split("-"); return new Date(Number(y),Number(m)-1,1).toLocaleDateString("en-IN",{month:"long",year:"numeric"}); };

function exportExitCSV(rows, filename) {
  const esc = v => `"${String(v||"").replace(/"/g,'""')}"`;
  const fmt = d => d ? new Date(d).toLocaleDateString("en-IN", { day:"2-digit", month:"short", year:"numeric" }) : "";
  const headers = [
    "Employee Name","Emp ID","Department","Role","Designation","Team",
    "Completed By (Admin)","Completed At",
    "Std Items Cleared","Assets Returned","Credentials Revoked","Total Items Checked","Month"
  ];
  const lines = [headers.join(",")];
  rows.forEach(h => {
    lines.push([
      h.emp_name, h.emp_emp_id, h.department||"", h.role||"", h.designation||"", h.team||"",
      h.admin_name, fmt(h.completed_at),
      h.std_items_cleared??h.std_cleared??"",
      h.assets_returned??"",
      h.credentials_revoked??h.creds_revoked??"",
      h.total_items_checked??h.total_items??"",
      monthLabel(monthKey(h.completed_at))
    ].map(esc).join(","));
  });
  const blob = new Blob([lines.join("\n")], {type:"text/csv;charset=utf-8;"});
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a"); a.href=url; a.download=filename; a.click();
  URL.revokeObjectURL(url);
}

function ITAExitPg() {
  const [employees, setEmployees] = useState([]);
  const [selEmp, setSelEmp] = useState(null);
  const [search, setSearch] = useState("");
  const [showDrop, setShowDrop] = useState(false);

  // Dynamic checklist items
  const [assetItems, setAssetItems] = useState([]);
  const [credItems, setCredItems] = useState([]);
  const [checklist, setChecklist] = useState({});
  const DEFAULT_EXIT_ITEMS = [
  { key: "std_idcard", label: "Collect ID Card", detail: "Physical identification card" },
  { key: "std_keys", label: "Collect Cabinet/Office Keys", detail: "Physical keys if any" },
  { key: "std_accesscard", label: "Deactivate Access Card", detail: "Biometric/RFID access" }];

  const [loading, setLoading] = useState(false);
  const [suc, setSuc] = useState("");
  const [sending, setSending] = useState(false);
  const [offboardingQueue, setOffboardingQueue] = useState([]);
  const [selectedOffReq, setSelectedOffReq] = useState('');
  const [itAmount, setItAmount] = useState('');
  const [itComment, setItComment] = useState('');

  // Completed exits history
  const [exitHistory, setExitHistory] = useState([]);
  const [exitMonthF,  setExitMonthF]  = useState("all");
  const [showExitMDrop, setShowExitMDrop] = useState(false);

  useEffect(() => {
    fetchEmployees();
    fetchExitHistory();
    fetchOffboardingQueue();
  }, []);

  // Re-fetch when month filter changes
  useEffect(() => {
    if (exitMonthF !== "all") {
      const [y, m] = exitMonthF.split("-");
      fetchExitHistory(Number(m), Number(y));
    } else {
      fetchExitHistory();
    }
  }, [exitMonthF]);

  const fetchEmployees = async () => {
    try {
      const data = await api.getITPeople();
      setEmployees((data || []).map((p) => ({
        id: p.id || p.emp_id,
        numId: p.numeric_id || p.user_id || "",
        name: p.name,
        dept: p.dept,
        role: p.role,
        status: p.status
      })));
    } catch (e) {console.warn(e);}
  };

  const fetchExitHistory = async (month, year) => {
    try {
      const data = await api.getExitHistory(month, year);
      setExitHistory(data || []);
    } catch (e) {console.warn(e);}
  };

  const fetchOffboardingQueue = async () => {
    try {
      const data = await api.getITOffboardingQueue();
      setOffboardingQueue(Array.isArray(data) ? data : []);
    } catch (e) {console.warn(e);}
  };

  const showSuccess = (msg) => {setSuc(msg);setTimeout(() => setSuc(""), 5000);};

  // When employee selected, fetch their assets + credentials + existing checklist
  const selectEmployee = async (emp) => {
    setSelEmp(emp);
    setSearch(emp.name);
    setShowDrop(false);
    setLoading(true);

    try {
      const [aData, cData, savedChecklist] = await Promise.all([
      api.getITAssets(),
      api.getITCredentials(),
      api.getExitChecklist(emp.numId || emp.id)]
      );

      // Filter assets for this employee
      const empAssets = (aData || []).filter((a) => {
        const assignedTo = a.assigned_to ? String(a.assigned_to) : "";
        return a.emp_name === emp.name || assignedTo === String(emp.numId) || assignedTo === emp.id;
      }).map((a) => ({
        key: `asset_${a.asset_id}`,
        type: "asset",
        id: a.asset_id,
        label: `Return ${a.asset_type}: ${a.brand}`,
        detail: `${a.asset_id} · Serial: ${a.serial} · Condition: ${a.condition}`,
        status: a.status
      }));

      // Filter credentials for this employee
      const empCred = (cData || []).find((c) => {
        const userId = c.user_id ? String(c.user_id) : "";
        return c.emp_name === emp.name || userId === String(emp.numId) || userId === emp.id;
      });

      const credChecks = [];
      if (empCred) {
        credChecks.push({
          key: "cred_laptop",
          type: "credential",
          label: "Revoke Laptop Access",
          detail: `Username: ${empCred.laptop_user || "—"}`
        });
        if (empCred.vpn_enabled) {
          credChecks.push({
            key: "cred_vpn",
            type: "credential",
            label: "Disable VPN Access",
            detail: "VPN currently enabled"
          });
        }
        if (empCred.email) {
          credChecks.push({
            key: "cred_email",
            type: "credential",
            label: "Disable Email Account",
            detail: empCred.email
          });
        }
        const software = typeof empCred.software === "string" ? JSON.parse(empCred.software || "[]") : empCred.software || [];
        if (software.length > 0) {
          credChecks.push({
            key: "cred_software",
            type: "credential",
            label: "Revoke Software Licenses",
            detail: software.join(", ")
          });
        }
        const aiTools = typeof empCred.ai_tools === "string" ? JSON.parse(empCred.ai_tools || "[]") : empCred.ai_tools || [];
        if (aiTools.length > 0) {
          credChecks.push({
            key: "cred_ai",
            type: "credential",
            label: "Revoke AI Tool Access",
            detail: aiTools.join(", ")
          });
        }
        const sysAccess = typeof empCred.system_access === "string" ? JSON.parse(empCred.system_access || "[]") : empCred.system_access || [];
        if (sysAccess.length > 0) {
          credChecks.push({
            key: "cred_sysaccess",
            type: "credential",
            label: "Revoke System Access",
            detail: sysAccess.join(", ")
          });
        }
      }

      setAssetItems(empAssets);
      setCredItems(credChecks);

      // Restore saved checklist state
      const saved = {};
      (savedChecklist || []).forEach((s) => {
        saved[s.item_key] = { checked: true, timestamp: s.checked_at };
      });
      setChecklist(saved);
    } catch (e) {console.warn("Error loading exit data:", e.message);} finally
    {setLoading(false);}
  };

  // Toggle checklist item
  const toggleItem = async (key) => {
    const isChecked = !!checklist[key]?.checked;
    const now = new Date().toISOString();

    if (!isChecked) {
      setChecklist((prev) => ({ ...prev, [key]: { checked: true, timestamp: now } }));
    } else {
      setChecklist((prev) => {
        const copy = { ...prev };
        delete copy[key];
        return copy;
      });
    }

    // Save to backend
    try {
      await api.updateExitItem({
        empId: selEmp.numId || selEmp.id,
        empName: selEmp.name,
        itemKey: key,
        checked: !isChecked
      });
    } catch (e) {console.warn(e);}
  };

  // Check if all items are completed
  const allItems = [...DEFAULT_EXIT_ITEMS, ...assetItems, ...credItems];
  const allChecked = allItems.length > 0 && allItems.every((item) => checklist[item.key]?.checked);
  const checkedCount = allItems.filter((item) => checklist[item.key]?.checked).length;

  // Send to HR
  const sendToHR = async () => {
    if (!allChecked || !selEmp) return;
    setSending(true);
    try {
      await api.completeExitChecklist({
        empId: selEmp.numId || selEmp.id,
        empName: selEmp.name,
        empEmpId: selEmp.id
      });
      showSuccess(`Exit checklist for ${selEmp.name} sent to HR successfully!`);
      setSelEmp(null);
      setSearch("");
      setAssetItems([]);
      setCredItems([]);
      setChecklist({});
      await fetchExitHistory();
    } catch (e) {alert("Error: " + e.message);} finally
    {setSending(false);}
  };

  const fmtTime = (t) => {
    if (!t) return "";
    const d = new Date(t);
    return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  const sendItReply = async () => {
    if (!selectedOffReq) return;
    const row = offboardingQueue.find((r) => r.req_id === selectedOffReq);
    if (!row) return;
    const available = row?.employee_payload?.assets?.available || [];
    const missing = row?.employee_payload?.assets?.missing || [];
    try {
      await api.itReplyOffboarding({
        reqId: selectedOffReq,
        payableAmount: Number(itAmount || 0),
        comment: itComment || '',
        availableAssets: available,
        missingAssets: missing
      });
      showSuccess('IT reply sent to HR and employee');
      setItAmount('');
      setItComment('');
      await fetchOffboardingQueue();
    } catch (e) {
      alert(e.message || 'Failed to send IT reply');
    }
  };

  // Filtered dropdown
  const filtered = employees.filter((e) =>
  e.name.toLowerCase().includes(search.toLowerCase()) ||
  e.id.toLowerCase().includes(search.toLowerCase())
  );

  // ── Exit history derived ──
  const exitAvailMonths = useMemo(()=>{
    const set=new Set(); exitHistory.forEach(h=>{ const k=monthKey(h.completed_at); if(k) set.add(k); });
    return ["all",...Array.from(set).sort((a,b)=>b.localeCompare(a))];
  },[exitHistory]);

  const filteredExit = useMemo(()=>{
    if (exitMonthF==="all") return exitHistory;
    return exitHistory.filter(h=>monthKey(h.completed_at)===exitMonthF);
  },[exitHistory,exitMonthF]);

  // Stat badges derived from filteredExit
  const exitStats = useMemo(() => {
    const total = filteredExit.length;
    const assetsSum = filteredExit.reduce((s,h)=>s+(Number(h.assets_returned)||0), 0);
    const credsSum  = filteredExit.reduce((s,h)=>s+(Number(h.credentials_revoked)||0), 0);
    const stdSum    = filteredExit.reduce((s,h)=>s+(Number(h.std_items_cleared)||0), 0);
    return { total, assetsSum, credsSum, stdSum };
  }, [filteredExit]);

  // Server-side CSV download with auth token
  const downloadServerCSV = (month, year, filename) => {
    const token = localStorage.getItem("token_itadmin") ||
      (() => { for (const r of ["superadmin","hr","pm","itadmin","employee","intern"]) { const t=localStorage.getItem("token_"+r); if(t) return t; } return ""; })();
    const params = new URLSearchParams();
    if (month) params.set("month", month);
    if (year)  params.set("year", year);
    const url = (process.env.REACT_APP_API_URL||"/api") + "/itadmin/exit-history/csv?" + params.toString();
    fetch(url, { headers: { Authorization: "Bearer " + token } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
      })
      .catch(() => exportExitCSV(filteredExit, filename));
  };

  return (
    <div>
      <div className="itaexitpg-inline-1">Exit Checklist</div>

      <div className="card animate-in itaexitpg-inline-2">
        <div className="card-header">
          <div className="card-title">Offboarding Asset Review Queue</div>
        </div>
        <div className="card-body">
          <select className="form-input itaexitpg-inline-3" value={selectedOffReq} onChange={(e) => setSelectedOffReq(e.target.value)}>
            <option value="">Select offboarding request</option>
            {offboardingQueue.map((r) =>
            <option key={r.req_id} value={r.req_id}>
                {r.req_id} - {r.user_name || '-'} ({r.status})
              </option>
            )}
          </select>
          {selectedOffReq && (() => {
            const row = offboardingQueue.find((r) => r.req_id === selectedOffReq);
            const available = row?.employee_payload?.assets?.available || [];
            const missing = row?.employee_payload?.assets?.missing || [];
            return (
              <div>
                <div className="itaexitpg-inline-4">
                  <div className="itaexitpg-inline-5">
                    <div className="itaexitpg-inline-6">Available Assets</div>
                    {available.length ? available.map((a, i) => <div key={i} className="itaexitpg-inline-7">{a}</div>) : <div className="itaexitpg-inline-8">None</div>}
                  </div>
                  <div className="itaexitpg-inline-9">
                    <div className="itaexitpg-inline-10">Missing Assets</div>
                    {missing.length ? missing.map((a, i) => <div key={i} className="itaexitpg-inline-11">{a}</div>) : <div className="itaexitpg-inline-12">None</div>}
                  </div>
                </div>
                <div className="form-row">
                  <input className="form-input" type="number" placeholder="Payable amount for missing assets" value={itAmount} onChange={(e) => setItAmount(e.target.value)} />
                </div>
                <textarea className="form-input itaexitpg-inline-13" rows={3} placeholder="IT comment" value={itComment} onChange={(e) => setItComment(e.target.value)} />
                <button className="btn-primary itaexitpg-inline-14" onClick={sendItReply}>Send IT Reply</button>
              </div>);

          })()}
        </div>
      </div>

      {suc &&
      <div className="itaexitpg-inline-15">
          <Sv d={ic.chk} s={16} c="#2563eb" /> {suc}
        </div>
      }

      {/* SEARCH BOX */}
      <div className="card animate-in itaexitpg-inline-16">
        <div className="card-header">
          <div className="card-title">Select Employee</div>
        </div>
        <div className="card-body itaexitpg-inline-17">
          <div className="itaexitpg-inline-18">
            <input
              className="form-input itaexitpg-inline-19"
              value={search}
              onChange={(e) => {setSearch(e.target.value);setShowDrop(true);setSelEmp(null);if (!e.target.value) {setAssetItems([]);setCredItems([]);setChecklist({});}}}
              onFocus={() => {if (!selEmp) setShowDrop(true);}}
              onBlur={() => setTimeout(() => setShowDrop(false), 400)}
              placeholder="🔍 Search employee by name or ID..." />

            
            {showDrop && !selEmp && filtered.length > 0 &&
            <div className="itaexitpg-inline-20"> {filtered.map((emp) =>
              <div
                key={emp.id}
                onClick={() => selectEmployee(emp)}

                onMouseEnter={(e) => e.currentTarget.style.background = "#f8fafc"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#fff"} className="itaexitpg-inline-21">
                
                  <div>
                    <div className="itaexitpg-inline-22">{emp.name}</div>
                    <div className="itaexitpg-inline-23">{emp.id} · {emp.dept} · {emp.role}</div>
                  </div>
                  <span style={{ "--itaexitpg-1-background":

                  emp.status === "ACTIVE" ? "#eff6ff" : emp.status === "EXITING" ? "#fef2f2" : "#f3f4f6", "--itaexitpg-1-color":
                  emp.status === "ACTIVE" ? "#2563eb" : emp.status === "EXITING" ? "#ef4444" : "#64748b" }} className="itaexitpg-dyn-1">
                  {emp.status}</span>
                </div>
              )}
              </div>
            }
            {showDrop && search && !selEmp && filtered.length === 0 &&
            <div className="itaexitpg-inline-24">No employees found</div>
            }
          </div>
        </div>
      </div>

      {/* CHECKLIST */}
      {loading &&
      <div className="itaexitpg-inline-25">Loading checklist...</div>
      }

      {selEmp && !loading &&
      <div>
          {/* Employee Info Header */}
          <div className="itaexitpg-inline-26">
            <div style={{ "--itaexitpg-2-top": `${-30}px`, "--itaexitpg-2-right": `${-30}px` }} className="itaexitpg-dyn-2" />
            <div className="itaexitpg-inline-27">
              <div>
                <div className="itaexitpg-inline-28">{selEmp.name}</div>
                <div className="itaexitpg-inline-29">{selEmp.role} · {selEmp.dept}</div>
                <span className="itaexitpg-inline-30">{selEmp.id}</span>
              </div>
              <div className="itaexitpg-inline-31">
                <div style={{ "--itaexitpg-3-color": allChecked ? "#60a5fa" : "#f59e0b" }} className="itaexitpg-dyn-3">{checkedCount}/{allItems.length}</div>
                <div className="itaexitpg-inline-32">items completed</div>
                {/* Progress bar */}
                <div className="itaexitpg-inline-33">
                  <div style={{ "--itaexitpg-4-width": `${allItems.length ? checkedCount / allItems.length * 100 : 0}%`, "--itaexitpg-4-background": allChecked ? "#60a5fa" : "#6366f1" }} className="itaexitpg-dyn-4" />
                </div>
              </div>
            </div>
          </div>

          {/* No items */}
          {allItems.length === 0 &&
        <div className="card animate-in">
              <div className="card-body itaexitpg-inline-34">
                <div className="itaexitpg-inline-35">📋</div>
                <div className="itaexitpg-inline-36">No tasks found</div>
                <div className="itaexitpg-inline-37">Check if employee selection is correct.</div>
              </div>
            </div>
        }

          {/* STANDARD ITEMS SECTION */}
          <div className="card animate-in itaexitpg-inline-38">
            <div className="card-header">
              <div>
                <div className="card-title itaexitpg-inline-39">
                  <Sv d={ic.chk} s={16} c="#6366f1" /> Standard Clearances
                </div>
                <div className="card-subtitle">{DEFAULT_EXIT_ITEMS.filter((i) => checklist[i.key]?.checked).length} of {DEFAULT_EXIT_ITEMS.length} completed</div>
              </div>
            </div>
            <div className="card-body itaexitpg-inline-40">
              {DEFAULT_EXIT_ITEMS.map((item, idx) => {
              const isChecked = !!checklist[item.key]?.checked;
              const ts = checklist[item.key]?.timestamp;
              return (
                <div key={item.key} style={{ "--itaexitpg-5-border-bottom":

                  idx < DEFAULT_EXIT_ITEMS.length - 1 ? "1px solid #f1f5f9" : "none", "--itaexitpg-5-background":
                  isChecked ? "#eff6ff" : "#fff" }} className="itaexitpg-dyn-5">

                  
                    <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(item.key)} className="itaexitpg-inline-41" />

                  
                    <div className="itaexitpg-inline-42">
                      <div style={{ "--itaexitpg-6-color": isChecked ? "#2563eb" : "#1e293b", "--itaexitpg-6-text-decoration": isChecked ? "line-through" : "none" }} className="itaexitpg-dyn-6">{item.label}</div>
                      <div className="itaexitpg-inline-43">{item.detail}</div>
                    </div>
                    {isChecked && ts &&
                  <div className="itaexitpg-inline-44">
                        <div>✓ Done</div>
                        <div className="itaexitpg-inline-45">{fmtTime(ts)}</div>
                      </div>
                  }
                  </div>);

            })}
            </div>
          </div>

          {/* ASSETS SECTION */}
          {assetItems.length > 0 &&
        <div className="card animate-in itaexitpg-inline-46">
              <div className="card-header">
                <div>
                  <div className="card-title itaexitpg-inline-47">
                    <Sv d={ic.bulk || ic.box} s={16} c="#6366f1" /> Asset Returns
                  </div>
                  <div className="card-subtitle">{assetItems.filter((a) => checklist[a.key]?.checked).length} of {assetItems.length} returned</div>
                </div>
              </div>
              <div className="card-body itaexitpg-inline-48">
                {assetItems.map((item, idx) => {
              const isChecked = !!checklist[item.key]?.checked;
              const ts = checklist[item.key]?.timestamp;
              return (
                <div key={item.key} style={{ "--itaexitpg-7-border-bottom":

                  idx < assetItems.length - 1 ? "1px solid #f1f5f9" : "none", "--itaexitpg-7-background":
                  isChecked ? "#eff6ff" : "#fff" }} className="itaexitpg-dyn-7">

                  
                      <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(item.key)} className="itaexitpg-inline-49" />

                  
                      <div className="itaexitpg-inline-50">
                        <div style={{ "--itaexitpg-8-color": isChecked ? "#2563eb" : "#1e293b", "--itaexitpg-8-text-decoration": isChecked ? "line-through" : "none" }} className="itaexitpg-dyn-8">{item.label}</div>
                        <div className="itaexitpg-inline-51">{item.detail}</div>
                      </div>
                      {isChecked && ts &&
                  <div className="itaexitpg-inline-52">
                          <div>✓ Returned</div>
                          <div className="itaexitpg-inline-53">{fmtTime(ts)}</div>
                        </div>
                  }
                    </div>);

            })}
              </div>
            </div>
        }

          {/* CREDENTIALS SECTION */}
          {credItems.length > 0 &&
        <div className="card animate-in itaexitpg-inline-54">
              <div className="card-header">
                <div>
                  <div className="card-title itaexitpg-inline-55">
                    <Sv d={ic.shield} s={16} c="#6366f1" /> Access & Credential Revocation
                  </div>
                  <div className="card-subtitle">{credItems.filter((c) => checklist[c.key]?.checked).length} of {credItems.length} revoked</div>
                </div>
              </div>
              <div className="card-body itaexitpg-inline-56">
                {credItems.map((item, idx) => {
              const isChecked = !!checklist[item.key]?.checked;
              const ts = checklist[item.key]?.timestamp;
              return (
                <div key={item.key} style={{ "--itaexitpg-9-border-bottom":

                  idx < credItems.length - 1 ? "1px solid #f1f5f9" : "none", "--itaexitpg-9-background":
                  isChecked ? "#eff6ff" : "#fff" }} className="itaexitpg-dyn-9">

                  
                      <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleItem(item.key)} className="itaexitpg-inline-57" />

                  
                      <div className="itaexitpg-inline-58">
                        <div style={{ "--itaexitpg-10-color": isChecked ? "#2563eb" : "#1e293b", "--itaexitpg-10-text-decoration": isChecked ? "line-through" : "none" }} className="itaexitpg-dyn-10">{item.label}</div>
                        <div className="itaexitpg-inline-59">{item.detail}</div>
                      </div>
                      {isChecked && ts &&
                  <div className="itaexitpg-inline-60">
                          <div>✓ Revoked</div>
                          <div className="itaexitpg-inline-61">{fmtTime(ts)}</div>
                        </div>
                  }
                    </div>);

            })}
              </div>
            </div>
        }

          {/* SEND TO HR BUTTON */}
          {allItems.length > 0 &&
        <div className="itaexitpg-inline-62">
              {allChecked ?
          <button
            onClick={sendToHR}
            disabled={sending} style={{ "--itaexitpg-11-cursor":


              sending ? "wait" : "pointer", "--itaexitpg-11-opacity":

              sending ? 0.7 : 1 }} className="itaexitpg-dyn-11">
            
            
                  <Sv d={ic.send || ic.chk} s={16} c="#fff" />
                  {sending ? "Sending..." : "✅ Complete — Send to HR"}
                </button> :

          <div className="itaexitpg-inline-63">
                  Complete all {allItems.length - checkedCount} remaining item{allItems.length - checkedCount !== 1 ? "s" : ""} to send to HR
                </div>
          }
            </div>
        }
        </div>
      }

      {/* EXIT HISTORY */}
      <div className="card animate-in itaexitpg-inline-64">
        <div className="card-header" style={{flexWrap:"wrap",gap:10}}>
          <div>
            <div className="card-title">Completed Exits</div>
            <div className="card-subtitle">{exitHistory.length} total processed</div>
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
            <select value={exitMonthF} onChange={e=>setExitMonthF(e.target.value)}
              style={{padding:"7px 10px",border:"1px solid #e2e8f0",borderRadius:8,
                fontSize:12,color:"#374151",background:"#fff",cursor:"pointer"}}>
              <option value="all">All Months</option>
              {exitAvailMonths.filter(m=>m!=="all").map(m=>(
                <option key={m} value={m}>{monthLabel(m)}</option>
              ))}
            </select>
            <div style={{position:"relative"}}>
              <button onClick={()=>setShowExitMDrop(v=>!v)}
                style={{display:"flex",alignItems:"center",gap:5,padding:"7px 12px",
                  background:"#fff",border:"1px solid #e2e8f0",borderRadius:8,
                  fontWeight:600,fontSize:12,color:"#374151",cursor:"pointer"}}>
                <Sv d={ic.cal} s={13} c="#374151"/> Monthly CSV ▾
              </button>
              {showExitMDrop && (
                <div style={{position:"absolute",right:0,top:"calc(100% + 6px)",background:"#fff",
                  border:"1px solid #e2e8f0",borderRadius:10,boxShadow:"0 8px 24px rgba(0,0,0,.12)",
                  zIndex:200,minWidth:230,overflow:"hidden"}}>
                  <div style={{padding:"8px 12px",fontSize:11,fontWeight:700,color:"#64748b",
                    borderBottom:"1px solid #f1f5f9",background:"#f8fafc",letterSpacing:1}}>SELECT MONTH</div>
                  {exitAvailMonths.filter(m=>m!=="all").length===0 ? (
                    <div style={{padding:"12px",fontSize:12,color:"#94a3b8"}}>No history yet</div>
                  ) : exitAvailMonths.filter(m=>m!=="all").map(m=>{
                    const mRows=exitHistory.filter(h=>monthKey(h.completed_at)===m);
                    const [y,mo]=m.split("-");
                    return (
                      <button key={m}
                        onClick={()=>{ downloadServerCSV(Number(mo),Number(y),`exit_report_${m}.csv`); setShowExitMDrop(false); }}
                        style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                          width:"100%",padding:"10px 14px",border:"none",background:"#fff",
                          cursor:"pointer",fontSize:13,borderBottom:"1px solid #f8fafc"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#f0f9ff"}
                        onMouseLeave={e=>e.currentTarget.style.background="#fff"}>
                        <span style={{fontWeight:600,color:"#1e293b"}}>{monthLabel(m)}</span>
                        <span style={{fontSize:11,color:"#94a3b8"}}>{mRows.length} exits</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <button onClick={()=>{
              if(exitMonthF!=="all"){const[y,m]=exitMonthF.split("-");downloadServerCSV(Number(m),Number(y),`exit_report_${exitMonthF}.csv`);}
              else downloadServerCSV(null,null,"exit_report_all.csv");
            }}
              style={{display:"flex",alignItems:"center",gap:6,padding:"7px 14px",
                background:"#0f172a",color:"#fff",border:"none",borderRadius:8,
                fontWeight:700,fontSize:12,cursor:"pointer",whiteSpace:"nowrap"}}>
              <Sv d={ic.dwn} s={13} c="#fff"/>
              Download CSV{filteredExit.length!==exitHistory.length?` (${filteredExit.length})`:""}
            </button>
          </div>
        </div>

        {exitMonthF!=="all" && (
          <div style={{background:"linear-gradient(135deg,#1e1b4b,#4f46e5)",
            padding:"10px 18px",display:"flex",justifyContent:"space-between",
            alignItems:"center",color:"#fff"}}>
            <div>
              <div style={{fontWeight:700,fontSize:13}}>📅 {monthLabel(exitMonthF)} — Exit Report</div>
              <div style={{fontSize:11,opacity:.8}}>{filteredExit.length} employees processed</div>
            </div>
            <button onClick={()=>{const[y,m]=exitMonthF.split("-");downloadServerCSV(Number(m),Number(y),`exit_report_${exitMonthF}.csv`);}}
              style={{padding:"6px 12px",background:"rgba(255,255,255,.2)",border:"1px solid rgba(255,255,255,.3)",
                borderRadius:8,color:"#fff",fontWeight:700,fontSize:12,cursor:"pointer",
                display:"flex",alignItems:"center",gap:5}}>
              <Sv d={ic.dwn} s={12} c="#fff"/> Download Month CSV
            </button>
          </div>
        )}

        {filteredExit.length>0 && (
          <div style={{display:"flex",gap:10,padding:"14px 18px",flexWrap:"wrap",borderBottom:"1px solid #f1f5f9"}}>
            {[
              ["Exits Processed",  exitStats.total,     "#4f46e5","#eef2ff"],
              ["Assets Returned",  exitStats.assetsSum,  "#0891b2","#ecfeff"],
              ["Creds Revoked",    exitStats.credsSum,   "#dc2626","#fef2f2"],
              ["Std Items Cleared",exitStats.stdSum,     "#059669","#ecfdf5"],
            ].map(([label,val,color,bg])=>(
              <div key={label} style={{background:bg,border:`1px solid ${color}33`,borderRadius:10,
                padding:"10px 16px",minWidth:110,textAlign:"center"}}>
                <div style={{fontSize:22,fontWeight:800,color}}>{val}</div>
                <div style={{fontSize:11,color:"#475569",fontWeight:600,marginTop:2}}>{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="card-body itaexitpg-inline-65" style={{overflowX:"auto"}}>
          {exitHistory.length===0 ? (
            <div style={{textAlign:"center",padding:30,color:"#94a3b8",fontSize:13}}>
              No completed exits yet
            </div>
          ) : filteredExit.length===0 ? (
            <div style={{textAlign:"center",padding:20,color:"#94a3b8",fontSize:13}}>
              No exits in {monthLabel(exitMonthF)}
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>#</th><th>Employee</th><th>Emp ID</th>
                  <th>Department</th><th>Role</th>
                  <th style={{textAlign:"center"}}>Std</th>
                  <th style={{textAlign:"center"}}>Assets</th>
                  <th style={{textAlign:"center"}}>Creds</th>
                  <th>Completed By</th><th>Completed At</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredExit.map((h,i)=>(
                  <tr key={i}>
                    <td style={{color:"#94a3b8",fontSize:11}}>{i+1}</td>
                    <td style={{fontWeight:600}}>{h.emp_name||"—"}</td>
                    <td style={{color:"#4f46e5",fontWeight:600,fontSize:12}}>{h.emp_emp_id||"—"}</td>
                    <td style={{fontSize:12,color:"#475569"}}>{h.department||"—"}</td>
                    <td style={{fontSize:12}}>{h.role||"—"}</td>
                    <td style={{textAlign:"center"}}>
                      <span style={{background:"#f0fdf4",color:"#16a34a",borderRadius:12,padding:"2px 8px",fontSize:11,fontWeight:600}}>
                        {h.std_items_cleared??h.std_cleared??"—"}
                      </span>
                    </td>
                    <td style={{textAlign:"center"}}>
                      <span style={{background:"#eff6ff",color:"#2563eb",borderRadius:12,padding:"2px 8px",fontSize:11,fontWeight:600}}>
                        {h.assets_returned??"—"}
                      </span>
                    </td>
                    <td style={{textAlign:"center"}}>
                      <span style={{background:"#fef2f2",color:"#dc2626",borderRadius:12,padding:"2px 8px",fontSize:11,fontWeight:600}}>
                        {h.credentials_revoked??h.creds_revoked??"—"}
                      </span>
                    </td>
                    <td style={{fontSize:12}}>{h.admin_name||"—"}</td>
                    <td style={{fontSize:11,color:"#64748b",whiteSpace:"nowrap"}}>{h.completed_at?fmtTime(h.completed_at):"—"}</td>
                    <td>
                      <span style={{background:"#ecfdf5",color:"#059669",padding:"2px 10px",borderRadius:20,fontSize:11,fontWeight:700}}>
                        ✓ Completed
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>);

}

export default ITAExitPg;
